// @ts-nocheck
/**
 * Client email preferences, managed by admin / superAdmin from the Edit Client modal.
 *
 * Mirrors the toggles the client sees on their own profile (Necessary, Optional and
 * "Block all emails"). Changes are kept locally and saved on the modal's Submit, via
 * the `save()` handle. "Edited by" is shown here only: the backend never returns it
 * to the client (`GET|PUT clientEmailPreference/:clientId`, admin-only routes).
 */
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { CustomSwitch } from "../../../components/Custom/Switch";
import { apiRequest } from "../../../services/apiService";
import {
  clientNecessaryEmailPreference,
  clientOptionalEmailPreference,
} from "../../../utils/constants/emailPreference";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";

const NECESSARY = "clientEmailNotificationNecessaryPreference";
const OPTIONAL = "clientEmailNotificationOptionalPreference";

const GROUPS = [
  { key: NECESSARY, title: "Necessary Emails", labels: clientNecessaryEmailPreference },
  { key: OPTIONAL, title: "Optional Emails", labels: clientOptionalEmailPreference },
];

const pink = () => getCssVariableHandler({ variableName: "--primary-pink-hex" });

// Same look as the other section titles of the Add/Edit Client modal.
const titleSx = { fontSize: "1rem", color: pink() };

function roleLabel(role) {
  if (role === "superAdmin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "client") return "Client";
  return "";
}

/** Only the toggles that differ from what was loaded, in the backend's shape. */
function buildChanges(original, current) {
  const body = {};
  if (Boolean(original?.blockAllEmails) !== Boolean(current?.blockAllEmails)) {
    body.blockAllEmails = Boolean(current?.blockAllEmails);
  }
  for (const { key, labels } of GROUPS) {
    for (const field of Object.keys(labels)) {
      const before = Boolean(original?.[key]?.[field]);
      const after = Boolean(current?.[key]?.[field]);
      if (before !== after) body[key] = { ...(body[key] || {}), [field]: after };
    }
  }
  return body;
}

const ClientEmailPreferencesSection = forwardRef(function ClientEmailPreferencesSection({ clientId }, ref) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [original, setOriginal] = useState(null);
  const [current, setCurrent] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setLoadError("");
    const { response, error } = await apiRequest({
      method: "GET",
      url: `clientEmailPreference/${clientId}`,
      isToken: true,
    });
    const data = response?.data?.data;
    if (data) {
      setOriginal(data);
      setCurrent(data);
    } else {
      setLoadError(error?.response?.data?.message || "Could not load the email preferences.");
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const changes = useMemo(() => buildChanges(original, current), [original, current]);
  const isDirty = Object.keys(changes).length > 0;

  // Called by the modal's Submit. Resolves `true` when there is nothing to save or the
  // save worked; `false` (with the error message) keeps the modal open.
  useImperativeHandle(
    ref,
    () => ({
      async save() {
        if (!isDirty) return { ok: true };
        const { response, error } = await apiRequest({
          method: "PUT",
          url: `clientEmailPreference/${clientId}`,
          data: changes,
          isToken: true,
        });
        const data = response?.data?.data;
        if (data) {
          setOriginal(data);
          setCurrent(data);
          return { ok: true };
        }
        return {
          ok: false,
          message: error?.response?.data?.message || "Could not save the client's email preferences.",
        };
      },
    }),
    [changes, clientId, isDirty],
  );

  function toggle(groupKey, field, value) {
    setCurrent((prev) => ({
      ...prev,
      [groupKey]: { ...(prev?.[groupKey] || {}), [field]: value },
    }));
  }

  const editedBy = original?.lastEditedBy;

  return (
    <Box
      sx={{
        border: `1px solid ${pink()}`,
        borderRadius: "0.5rem",
        p: 2,
        mb: 2,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
          <Typography sx={titleSx}>Email Preferences:</Typography>
          {isDirty && (
            <Typography sx={{ fontSize: "0.8rem", color: pink() }}>Unsaved changes, click Submit to save</Typography>
          )}
        </Grid>

        {loading ? (
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} sx={{ color: pink() }} />
          </Grid>
        ) : loadError ? (
          <Grid item xs={12}>
            <Typography sx={{ fontSize: "0.9rem" }}>{loadError}</Typography>
            <Typography
              component="button"
              type="button"
              onClick={load}
              sx={{ background: "none", border: "none", color: pink(), cursor: "pointer", p: 0, mt: 1, textDecoration: "underline" }}
            >
              Try again
            </Typography>
          </Grid>
        ) : (
          <>
            {GROUPS.map(({ key, title, labels }) => (
              <Grid item xs={12} sm={6} key={key}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
                {Object.entries(labels).map(([field, label]) => (
                  <Box
                    key={field}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}
                  >
                    <Typography sx={{ fontSize: "0.9rem" }}>{label}</Typography>
                    <CustomSwitch
                      checked={Boolean(current?.[key]?.[field])}
                      onChange={(e) => toggle(key, field, e?.target?.checked)}
                    />
                  </Box>
                ))}
              </Grid>
            ))}

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                <Typography sx={{ fontSize: "0.9rem" }}>Block all emails</Typography>
                <CustomSwitch
                  checked={Boolean(current?.blockAllEmails)}
                  onChange={(e) => setCurrent((prev) => ({ ...prev, blockAllEmails: e?.target?.checked }))}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}>
                {editedBy
                  ? `Edited by ${editedBy.name}${roleLabel(editedBy.role) ? ` (${roleLabel(editedBy.role)})` : ""}${
                      editedBy.at ? ` on ${dayjs(editedBy.at).format("MMM D, YYYY h:mm A")}` : ""
                    }`
                  : "Edited by: no changes recorded yet"}
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
});

export default ClientEmailPreferencesSection;
